import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Tree, 
  Row,
  Col,
  Descriptions,
  App,
  Switch,
  Modal,
  Popconfirm,
  List,
  Tooltip,
  Table,
  Input,
  Select,
  Spin
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderOutlined,
  FileOutlined,
  BgColorsOutlined,
  CloudServerOutlined,
  PlusOutlined,
  DeleteOutlined,
  GlobalOutlined,
  ReloadOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { websiteApi } from '../services/api';
import { cloudflareApi } from '../services/cloudflareApi';
import WebsitePreview from '../components/WebsitePreview';
import type { GaTrafficSnapshot } from '../types/website';

const { Title, Text } = Typography;

// Function to detect file type based on extension
const getFileLanguage = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();
  
  const languageMap: { [key: string]: string } = {
    'js': 'javascript',
    'jsx': 'jsx',
    'ts': 'typescript',
    'tsx': 'tsx',
    'html': 'html',
    'htm': 'html',
    'css': 'css',
    'scss': 'scss',
    'sass': 'sass',
    'less': 'less',
    'json': 'json',
    'xml': 'xml',
    'yaml': 'yaml',
    'yml': 'yaml',
    'md': 'markdown',
    'py': 'python',
    'java': 'java',
    'cpp': 'cpp',
    'c': 'c',
    'php': 'php',
    'rb': 'ruby',
    'go': 'go',
    'rs': 'rust',
    'sql': 'sql',
    'sh': 'bash',
    'bash': 'bash',
    'zsh': 'bash',
    'dockerfile': 'dockerfile',
    'txt': 'text',
    'log': 'text'
  };
  
  return languageMap[extension || ''] || 'text';
};

// Convert API response to Ant Design Tree format
const convertToTreeData = (node: any): any[] => {
  if (!node.children || node.children.length === 0) {
    return [];
  }

  return node.children.map((child: any) => ({
    key: child.path || child.name,
    title: child.name,
    name: child.name, // Keep name for backward compatibility
    type: child.type,
    path: child.path,
    size: child.size,
    children: child.children ? convertToTreeData(child) : undefined,
  }));
};

const WebsiteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();



  // File viewer state
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [fileLoading, setFileLoading] = useState(false);

  // Preview modal state
  const [previewVisible, setPreviewVisible] = useState(false);
  
  // Cloudflare onboard state
  const [onboardModalVisible, setOnboardModalVisible] = useState(false);
  const [onboardResult, setOnboardResult] = useState<any>(null);
  const [isDomainOnboarded, setIsDomainOnboarded] = useState<boolean | null>(null);
  
  // DNS management state
  const [dnsModalVisible, setDnsModalVisible] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<Array<{
    key: string;
    type: string;
    name: string;
    content: string;
    proxied: boolean;
    ttl: number | 'auto';
    recordId?: string; // Cloudflare record ID for updates
  }>>([]);
  const [loadingDNSRecords, setLoadingDNSRecords] = useState(false);


  // Detect file language for syntax highlighting
  const fileLanguage = useMemo(() => {
    if (!selectedFile) return 'text';
    return getFileLanguage(selectedFile);
  }, [selectedFile]);

  const { data: website, isLoading } = useQuery(
    ['website', id],
    () => websiteApi.getById(id!),
    { enabled: !!id }
  );

  const { data: gaTrafficData, isLoading: gaTrafficLoading } = useQuery(
    ['website-ga-traffic', id],
    () => websiteApi.getGaTraffic(id!),
    { enabled: !!id && !!website?.ga_tag_key }
  );

  const syncGaTrafficMutation = useMutation(
    () => websiteApi.syncGaTraffic(id!),
    {
      onSuccess: () => {
        message.success('GA traffic synced');
        queryClient.invalidateQueries(['website-ga-traffic', id]);
        queryClient.invalidateQueries(['website', id]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to sync GA traffic');
        queryClient.invalidateQueries(['website-ga-traffic', id]);
      },
    }
  );

  const { data: fileTree, isLoading: filesLoading } = useQuery(
    ['website-files-tree', id],
    () => websiteApi.getFilesTree(id!),
    { enabled: !!id }
  );

  // Check Cloudflare domain status
  const { isLoading: checkingCloudflare } = useQuery(
    ['cloudflare-status', website?.domain],
    () => cloudflareApi.checkDomain(website!.domain),
    {
      enabled: !!website?.domain,
      onSuccess: (data) => {
        setIsDomainOnboarded(data.isOnboarded);
        // Invalidate queries to refresh data with updated cloudflare status and zoneId
        queryClient.invalidateQueries('websites'); // Refresh websites list
        queryClient.invalidateQueries(['website', id]); // Refresh current website detail
      },
      onError: () => {
        // If check fails, assume not onboarded (don't disable button)
        setIsDomainOnboarded(false);
      },
      retry: false, // Don't retry on error
    }
  );

  // Update website mutation
  const updateWebsiteMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => websiteApi.update(id, data),
    {
      onSuccess: () => {
        message.success('Website updated successfully!');
        queryClient.invalidateQueries(['website', id]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update website');
      },
    }
  );

  const handleToggleDirLinks = (checked: boolean) => {
    if (website) {
      updateWebsiteMutation.mutate({
        id: website.id,
        data: { hasDirLinks: checked }
      });
    }
  };

  // Cloudflare onboard mutation
  const onboardMutation = useMutation(
    (domain: string) => cloudflareApi.onboardDomain(domain),
    {
      onSuccess: (data) => {
        setOnboardResult(data);
        setOnboardModalVisible(true);
        if (data.success) {
          message.success(data.message);
          // Update domain status after successful onboard
          setIsDomainOnboarded(true);
          queryClient.invalidateQueries(['cloudflare-status', website?.domain]);
          queryClient.invalidateQueries('websites'); // Refresh websites list
        } else if (data.alreadyOnboarded) {
          message.warning(data.message);
          // Update domain status if already onboarded
          setIsDomainOnboarded(true);
          queryClient.invalidateQueries(['cloudflare-status', website?.domain]);
          queryClient.invalidateQueries('websites'); // Refresh websites list
        }
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to onboard domain to Cloudflare');
      },
    }
  );

  const handleOnboardToCloudflare = () => {
    if (website?.domain) {
      onboardMutation.mutate(website.domain);
    }
  };

  // Load DNS records from Cloudflare
  const { isLoading: loadingDNS, refetch: refetchDNS } = useQuery(
    ['cloudflare-dns', website?.domain],
    () => cloudflareApi.getDNSRecords(website!.domain),
    {
      enabled: false, // Don't auto-fetch, only when modal opens
      onSuccess: (data) => {
        // Check if domain has any DNS records
        if (data && data.records && Array.isArray(data.records) && data.records.length > 0) {
          // Convert Cloudflare records to table format
          const records = data.records.map((record: any, index: number) => ({
            key: record.id || `record_${index}`,
            type: record.type,
            name: record.name,
            content: record.content,
            proxied: ['A', 'AAAA', 'CNAME'].includes(record.type) ? (record.proxied !== undefined ? record.proxied : true) : false, // Auto checked for A, AAAA, CNAME if not set
            ttl: record.ttl === 1 ? 'auto' as const : record.ttl,
            recordId: record.id,
          }));
          setDnsRecords(records);
        } else {
          // No existing DNS records, init with default 3 rows
          initDefaultDNSRecords();
        }
        setLoadingDNSRecords(false);
      },
      onError: (error: any) => {
        console.error('Failed to load DNS records:', error);
        // If error loading (e.g., 403 permission error), still init default 3 rows
        initDefaultDNSRecords();
        setLoadingDNSRecords(false);
      },
    }
  );

  // Initialize DNS records with default 3 rows
  const initDefaultDNSRecords = () => {
    if (!website?.domain) return;
    
    const defaultRecords = [
      {
        key: '1',
        type: 'A',
        name: '*',
        content: '',
        proxied: true, // Auto checked
        ttl: 'auto' as const,
      },
      {
        key: '2',
        type: 'A',
        name: 'www',
        content: '',
        proxied: true, // Auto checked
        ttl: 'auto' as const,
      },
      {
        key: '3',
        type: 'A',
        name: website.domain,
        content: '',
        proxied: true, // Auto checked
        ttl: 'auto' as const,
      },
    ];
    
    setDnsRecords(defaultRecords);
  };

  // Initialize DNS modal
  const handleInitDNS = () => {
    if (!website?.domain) return;
    
    setDnsModalVisible(true);
    setLoadingDNSRecords(true);
    setDnsRecords([]); // Clear previous records
    
    // Fetch existing DNS records
    refetchDNS();
  };

  // Add new DNS record row
  const handleAddDNSRecord = () => {
    const newRecord = {
      key: Date.now().toString(),
      type: 'A',
      name: '',
      content: '',
      proxied: true, // Auto checked
      ttl: 'auto' as const,
    };
    setDnsRecords([...dnsRecords, newRecord]);
  };

  // Remove DNS record row
  const handleRemoveDNSRecord = (key: string) => {
    setDnsRecords(dnsRecords.filter(record => record.key !== key));
  };

  // Update DNS record
  const handleUpdateDNSRecord = (key: string, field: string, value: any) => {
    setDnsRecords(dnsRecords.map(record => 
      record.key === key ? { ...record, [field]: value } : record
    ));
  };

  // Sync DNS records mutation
  const syncDNSMutation = useMutation(
    (records: Array<{ type: string; name: string; content: string; ttl?: number; proxied?: boolean }>) => 
      cloudflareApi.syncDNSRecords(website!.domain, records),
    {
      onSuccess: (data) => {
        if (data.failed === 0) {
          message.success(`Successfully synced ${data.success} DNS records to Cloudflare!`);
        } else {
          message.warning(`Synced ${data.success} records. ${data.failed} failed.`);
          if (data.errors && data.errors.length > 0) {
            console.error('DNS sync errors:', data.errors);
          }
        }
        // Refetch DNS records after sync to show updated data
        refetchDNS();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to sync DNS records to Cloudflare');
      },
    }
  );

  // Handle sync DNS records
  const handleSyncDNS = () => {
    // Validate records
    const invalidRecords = dnsRecords.filter(record => 
      !record.type || !record.name || !record.content
    );

    if (invalidRecords.length > 0) {
      message.error('Please fill in all required fields (Type, Name, Target) for all DNS records');
      return;
    }

    // Prepare records for API
    const recordsToSync = dnsRecords.map(record => ({
      type: record.type,
      name: record.name,
      content: record.content,
      ttl: record.ttl === 'auto' ? 1 : record.ttl,
      proxied: ['A', 'AAAA', 'CNAME'].includes(record.type) ? record.proxied : undefined,
    }));

    syncDNSMutation.mutate(recordsToSync);
  };




  const handleEdit = () => {
    if (website) {
      navigate(`/websites/${website.id}/edit`);
    }
  };




  const handleFileSelect = async (filePath: string) => {
    if (!website) return;
    
    setSelectedFile(filePath);
    setFileLoading(true);
    setFileContent('');
    
    try {
      const content = await websiteApi.getFileContent(website.id, filePath);
      setFileContent(content);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load file content');
      setFileContent('Error loading file content');
    } finally {
      setFileLoading(false);
    }
  };


  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'green', text: 'Active' },
      inactive: { color: 'orange', text: 'Inactive' },
      building: { color: 'blue', text: 'Building' },
      error: { color: 'red', text: 'Error' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!website) {
    return <div>Website not found</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/websites')}
          style={{ marginRight: 16 }}
        >
          Back to Websites
        </Button>
        <Title level={2} style={{ display: 'inline' }}>
          {website.name}
        </Title>
        <div style={{ float: 'right' }}>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Website Information">
            <Descriptions column={1}>
              <Descriptions.Item label="Name">{website.name}</Descriptions.Item>
              <Descriptions.Item label="Domain">{website.domain}</Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag>{website.type?.toUpperCase() || 'Unknown'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(website.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Active">
                <Tag color={website.isActive ? 'green' : 'red'}>
                  {website.isActive ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Already Dir Links?">
                <Switch
                  checked={website.hasDirLinks || false}
                  onChange={handleToggleDirLinks}
                  loading={updateWebsiteMutation.isLoading}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Descriptions.Item>
              <Descriptions.Item label="Theme">
                <Tag color="purple" icon={<BgColorsOutlined />}>
                  {website.theme || 'Default'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(website.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {new Date(website.updatedAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Website Path">
                <Text code style={{ fontSize: '12px' }}>
                  {website.filePath || `/static-sites/websites/${website.domain}`}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            
            {website.description && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Description:</Text>
                <div style={{ marginTop: 8 }}>{website.description}</div>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                block
                onClick={() => navigate(`/websites/${id}/builder`)}
              >
                Website Builder
              </Button>
              {fileTree && fileTree.children && fileTree.children.length > 0 && (
                <>
                  <Button 
                    type="primary" 
                    icon={<EyeOutlined />}
                    block
                    onClick={() => setPreviewVisible(true)}
                  >
                    Preview Website
                  </Button>

                  <Button 
                    icon={<EyeOutlined />}
                    block
                    onClick={() => window.open(`http://localhost/${website.domain}`, '_blank')}
                  >
                    Open in New Tab
                  </Button>
                </>
              )}
              
              <Tooltip
                title={
                  isDomainOnboarded
                    ? `Domain "${website?.domain}" is already onboarded to Cloudflare`
                    : checkingCloudflare
                    ? 'Checking domain status...'
                    : undefined
                }
              >
                <Popconfirm
                  title="Onboard Domain to Cloudflare"
                  description={`Are you sure you want to onboard "${website?.domain}" to Cloudflare? Make sure you have configured the Cloudflare API Token in Settings.`}
                  onConfirm={handleOnboardToCloudflare}
                  okText="Yes, Onboard"
                  cancelText="Cancel"
                  okButtonProps={{ type: 'primary' }}
                  disabled={isDomainOnboarded === true}
                >
                  <Button 
                    icon={<CloudServerOutlined />}
                    block
                    loading={onboardMutation.isLoading || checkingCloudflare}
                    disabled={isDomainOnboarded === true}
                  >
                    {isDomainOnboarded
                      ? 'Domain Already Onboarded to CloudFlare'
                      : 'Onboard domain to CloudFlare'}
                  </Button>
                </Popconfirm>
              </Tooltip>
              
              {isDomainOnboarded && (
                <Button 
                  icon={<GlobalOutlined />}
                  block
                  onClick={handleInitDNS}
                >
                  Init DNS to CloudFlare
                </Button>
              )}
              
              <Button 
                icon={<DownloadOutlined />}
                block
              >
                Download Files
              </Button>
              
              <Button 
                icon={<EyeOutlined />}
                onClick={() => setPreviewVisible(true)}
                block
              >
                Preview Website
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {website.ga_tag_key && (
        <Card
          title={
            <Space size="middle" split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              <Space>
                <LineChartOutlined />
                <span>GA Traffic</span>
                <Tag color="blue">{website.ga_tag_key}</Tag>
              </Space>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={syncGaTrafficMutation.isLoading}
                onClick={() => syncGaTrafficMutation.mutate()}
              >
                Sync
              </Button>
            </Space>
          }
          style={{ marginTop: 16 }}
        >
          {gaTrafficLoading ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spin />
            </div>
          ) : (
            (() => {
              const traffic: GaTrafficSnapshot | null | undefined =
                gaTrafficData?.ga_traffic || website.ga_traffic;

              return (
                <>
                  {traffic?.sync_error && (
                    <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
                      Sync error: {traffic.sync_error}
                    </Text>
                  )}
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Last synced:{' '}
                    {traffic?.last_synced_at
                      ? new Date(traffic.last_synced_at).toLocaleString()
                      : 'Never — click Sync to pull from GA4'}
                  </Text>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Card size="small" title="Today" type="inner">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Users">
                            {(traffic?.users_today ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Sessions">
                            {(traffic?.sessions_today ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Pageviews">
                            {(traffic?.pageviews_today ?? 0).toLocaleString()}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card size="small" title="Last 7 days" type="inner">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Users">
                            {(traffic?.users_7d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Sessions">
                            {(traffic?.sessions_7d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Pageviews">
                            {(traffic?.pageviews_7d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card size="small" title="Last 30 days" type="inner">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Users">
                            {(traffic?.users_30d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Sessions">
                            {(traffic?.sessions_30d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Pageviews">
                            {(traffic?.pageviews_30d ?? 0).toLocaleString()}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>

                  <Table
                    style={{ marginTop: 16 }}
                    size="small"
                    pagination={false}
                    rowKey="date"
                    dataSource={traffic?.daily_7d || []}
                    locale={{ emptyText: 'No daily data yet' }}
                    columns={[
                      { title: 'Date', dataIndex: 'date', key: 'date' },
                      {
                        title: 'Users',
                        dataIndex: 'users',
                        key: 'users',
                        render: (v: number) => (v ?? 0).toLocaleString(),
                      },
                      {
                        title: 'Sessions',
                        dataIndex: 'sessions',
                        key: 'sessions',
                        render: (v: number) => (v ?? 0).toLocaleString(),
                      },
                      {
                        title: 'Pageviews',
                        dataIndex: 'pageviews',
                        key: 'pageviews',
                        render: (v: number) => (v ?? 0).toLocaleString(),
                      },
                    ]}
                  />
                </>
              );
            })()
          )}
        </Card>
      )}

      <Card title="File Management" style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Browse all files in the website directory structure
          </Text>
        </div>

        {filesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            Loading files...
          </div>
        ) : fileTree && fileTree.children && fileTree.children.length > 0 ? (
          <Row gutter={16}>
            <Col xs={24} lg={8}>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: '6px', padding: '12px' }}>
                <Text strong style={{ marginBottom: '12px', display: 'block' }}>File Tree</Text>
                <Tree
                  showLine
                  defaultExpandAll
                  treeData={convertToTreeData(fileTree)}
                  titleRender={(nodeData: any) => (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <div 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          cursor: nodeData.type === 'file' ? 'pointer' : 'default',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s',
                          flex: 1
                        }}
                        onClick={() => {
                          if (nodeData.type === 'file') {
                            handleFileSelect(nodeData.path);
                          }
                        }}
                      >
                        {nodeData.type === 'directory' ? (
                          <FolderOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        ) : (
                          <FileOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                        )}
                        <span style={{ 
                          color: nodeData.type === 'file' ? '#1890ff' : 'inherit',
                          textDecoration: nodeData.type === 'file' ? 'underline' : 'none'
                        }}>
                          {nodeData.title}
                        </span>
                        {nodeData.type === 'file' && nodeData.size && (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            ({(nodeData.size / 1024).toFixed(1)} KB)
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                />
              </div>
            </Col>
            <Col xs={24} lg={16}>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: '6px', padding: '12px', height: '400px' }}>
                <Text strong style={{ marginBottom: '12px', display: 'block' }}>
                  File Content {selectedFile && `- ${selectedFile.split('/').pop()}`}
                </Text>
                {selectedFile ? (
                  <div style={{ height: 'calc(100% - 40px)', overflow: 'auto' }}>
                    {fileLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        Loading file content...
                      </div>
                    ) : (
                      <div style={{ 
                        height: '100%',
                        overflow: 'auto',
                        borderRadius: '4px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <SyntaxHighlighter
                          language={fileLanguage}
                          style={tomorrow}
                          customStyle={{
                            margin: 0,
                            padding: '12px',
                            fontSize: '12px',
                            lineHeight: '1.4',
                            height: '100%',
                            overflow: 'auto'
                          }}
                          showLineNumbers={true}
                          wrapLines={true}
                          wrapLongLines={true}
                        >
                          {fileContent}
                        </SyntaxHighlighter>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 0', 
                    color: '#666',
                    height: 'calc(100% - 40px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <FileOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
                    <div style={{ fontSize: '16px', marginBottom: 8 }}>No file selected</div>
                    <div style={{ fontSize: '14px' }}>Click on a file in the tree to view its content</div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <div style={{ fontSize: '16px', marginBottom: 8 }}>No files found</div>
            <div style={{ fontSize: '14px' }}>This website doesn't have any files yet</div>
          </div>
        )}
      </Card>


      {/* Website Preview Modal */}
      <WebsitePreview
        websiteId={website?.id || ''}
        websiteDomain={website?.domain || ''}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
      />

      {/* Cloudflare Onboard Result Modal */}
      <Modal
        title="Cloudflare Onboard Result"
        open={onboardModalVisible}
        onCancel={() => {
          setOnboardModalVisible(false);
          setOnboardResult(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setOnboardModalVisible(false);
            setOnboardResult(null);
          }}>
            Close
          </Button>
        ]}
        width={600}
      >
        {onboardResult && (
          <div>
            {onboardResult.success ? (
              <>
                <p style={{ marginBottom: 16 }}>
                  <Tag color="green">Success</Tag> {onboardResult.message}
                </p>
                
                {onboardResult.zoneId && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Zone ID: </Text>
                    <Text code>{onboardResult.zoneId}</Text>
                  </div>
                )}
                
                {onboardResult.nameservers && onboardResult.nameservers.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Nameservers:</Text>
                    <List
                      size="small"
                      dataSource={onboardResult.nameservers}
                      renderItem={(ns: string) => (
                        <List.Item>
                          <Text code>{ns}</Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
                
                {onboardResult.nextSteps && onboardResult.nextSteps.length > 0 && (
                  <div>
                    <Text strong>Next Steps:</Text>
                    <List
                      size="small"
                      dataSource={onboardResult.nextSteps}
                      renderItem={(step: string, index: number) => (
                        <List.Item>
                          <Text>{index + 1}. {step}</Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
              </>
            ) : onboardResult.alreadyOnboarded ? (
              <p>
                <Tag color="orange">Already Onboarded</Tag> {onboardResult.message}
              </p>
            ) : (
              <p>
                <Tag color="red">Error</Tag> {onboardResult.message}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* DNS Management Modal */}
      <Modal
        title="Init DNS to CloudFlare"
        open={dnsModalVisible}
        onCancel={() => {
          setDnsModalVisible(false);
          setDnsRecords([]);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setDnsModalVisible(false);
            setDnsRecords([]);
          }}>
            Cancel
          </Button>,
          <Button 
            key="add" 
            icon={<PlusOutlined />}
            onClick={handleAddDNSRecord}
          >
            Add Record
          </Button>,
          <Button 
            key="sync" 
            type="primary"
            loading={syncDNSMutation.isLoading}
            onClick={handleSyncDNS}
          >
            Sync DNS to CloudFlare
          </Button>
        ]}
        width={1000}
      >
        {loadingDNSRecords ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Loading DNS records from Cloudflare...</Text>
            </div>
          </div>
        ) : (
          <Table
            dataSource={dnsRecords}
            loading={loadingDNS}
            columns={[
            {
              title: 'Type (required)',
              dataIndex: 'type',
              key: 'type',
              width: 150,
              render: (text: string, record: any) => (
                <Select
                  value={text}
                  onChange={(value) => handleUpdateDNSRecord(record.key, 'type', value)}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="A">A</Select.Option>
                  <Select.Option value="AAAA">AAAA</Select.Option>
                  <Select.Option value="CAA">CAA</Select.Option>
                  <Select.Option value="CERT">CERT</Select.Option>
                  <Select.Option value="CNAME">CNAME</Select.Option>
                  <Select.Option value="DNSKEY">DNSKEY</Select.Option>
                  <Select.Option value="DS">DS</Select.Option>
                  <Select.Option value="HTTPS">HTTPS</Select.Option>
                  <Select.Option value="LOC">LOC</Select.Option>
                  <Select.Option value="MX">MX</Select.Option>
                  <Select.Option value="NAPTR">NAPTR</Select.Option>
                  <Select.Option value="NS">NS</Select.Option>
                  <Select.Option value="PTR">PTR</Select.Option>
                  <Select.Option value="SMIMEA">SMIMEA</Select.Option>
                  <Select.Option value="SRV">SRV</Select.Option>
                  <Select.Option value="SSHFP">SSHFP</Select.Option>
                  <Select.Option value="SVCB">SVCB</Select.Option>
                  <Select.Option value="TLSA">TLSA</Select.Option>
                  <Select.Option value="TXT">TXT</Select.Option>
                  <Select.Option value="URI">URI</Select.Option>
                </Select>
              ),
            },
            {
              title: 'Name (required)',
              dataIndex: 'name',
              key: 'name',
              width: 200,
              render: (text: string, record: any) => (
                <Input
                  value={text}
                  onChange={(e) => handleUpdateDNSRecord(record.key, 'name', e.target.value)}
                  placeholder="e.g., www, *, @"
                />
              ),
            },
            {
              title: 'Target (required, unique)',
              dataIndex: 'content',
              key: 'content',
              width: 250,
              render: (text: string, record: any) => (
                <Input
                  value={text}
                  onChange={(e) => handleUpdateDNSRecord(record.key, 'content', e.target.value)}
                  placeholder="e.g., 192.0.2.1"
                />
              ),
            },
            {
              title: 'Proxy Status',
              dataIndex: 'proxied',
              key: 'proxied',
              width: 120,
              render: (proxied: boolean, record: any) => {
                const canProxy = ['A', 'AAAA', 'CNAME'].includes(record.type);
                return canProxy ? (
                  <Switch
                    checked={proxied}
                    onChange={(checked) => handleUpdateDNSRecord(record.key, 'proxied', checked)}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                ) : (
                  <Tag color="default">N/A</Tag>
                );
              },
            },
            {
              title: 'TTL',
              dataIndex: 'ttl',
              key: 'ttl',
              width: 100,
              render: (ttl: number | 'auto', record: any) => (
                <Tag color="blue">Auto</Tag>
              ),
            },
            {
              title: 'Action',
              key: 'action',
              width: 80,
              render: (_: any, record: any) => (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveDNSRecord(record.key)}
                />
              ),
            },
          ]}
          pagination={false}
          rowKey="key"
        />
        )}
      </Modal>

    </div>
  );
};

export default WebsiteDetail;
